/// <reference types="@swisscom/sdx/dist/js/webcomponents/types/components" />

import { default as SdxSelect } from 'sdx/form/Select';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { zeroFill } from 'lib/util/helpers';

const scDate = window.SCS.utils.date;

function clear<E extends Element>(el : E | undefined) : E | undefined {
	if(!el) {
		return undefined;
	}

	while (el.firstChild) {
		el.removeChild(el.firstChild);
	}

	return el;
}

/**
 * SDX Date Picker.
 *
 * @author Kiril Delovski
 * @class SDXDatePicker
 * @extends Tc.Module
 */
export default class SDXDatePicker extends Module {
	private constraints! : ScForm.DateConstraintData;
	private $hiddenInput! : JQuery<HTMLInputElement>;

	private timeInterval! : number;
	private showTimeInterval! : boolean;

	private sdxTimePicker? : SdxSelect;
	public $timeContainer! : JQuery;

	private timeFrom! : number;
	private timeTo! : number;
	private cv! : ScForm.CV<'DATE'>;
	public isReadOnly! : boolean;
	public showCurrentDate! : boolean;

	public on(callback : Hook) {
		const now = new Date();
		const $field = $('>.date-field', this.$ctx);
		const $input = $<HTMLSdxInputElement>('sdx-input[type="date"]', this.$ctx);
		this.$hiddenInput = this.$ctx.find(`input[data-name="${$input.attr('name')}"]`) as JQuery<HTMLInputElement>;

		// Container for time field options
		const timeSelect = this.$ctx.find('.time-container select')[0] as HTMLSelectElement;
		if (timeSelect) {
			this.sdxTimePicker = new SdxSelect(timeSelect);
		}

		// Snap appointment dates to this value (in minutes, defaults to 30)
		this.timeInterval = parseInt(this.$ctx.data('time-interval') || 30, 10);
		// Option to show time field values as from-to ranges in drop-down
		this.showTimeInterval = this.$ctx.data('show-time-interval') || false;
		// Config for SCS.utils.date
		this.constraints = {
			// Business hours
			day: {},
			hours: [
				scDate.toHours(this.$ctx.data('time-from') || 8),
				scDate.toHours(this.$ctx.data('time-to') || 17),
			],
			includeClosingTime: !this.showTimeInterval,
			interval: this.timeInterval / 60,
			// Date constraints
			maxDate: $input.data('enable-until'),
			minDate: $input.data('enable-from'),
			// Time constraints
			refTimeField: this.sdxTimePicker?.element,
			time: !!this.sdxTimePicker,
			week: [
				true, // Sun
				true, // mon
				true, // tue
				true, // wed
				true, // thu
				true, // fri
				true, // Sat
			],
		};

		// Upgrade for utils.date compatible config
		// Create exclude days configuration (D.M[.Y])
		const exclDateDays = $input.data('exclude-days') || [] as number[];
		for (const exclDateDay of exclDateDays) {
			// Normalize DD.MM.YYYY to D.M.Y (remove leading zeros)
			const dayDate = exclDateDay.replace(/(^|\.)0+/g, '$1');
			this.constraints.day![dayDate] = false;
		}

		// Create weekday configuration (weekday in java form)
		// Get a list of weekdays to skip (start from 1 = sun)
		const exclWeekDays = $input.data('no-weekdays') || [];
		for (const exclWeekDay of exclWeekDays) {
			this.constraints.week![exclWeekDay - 1] = false;
		}

		// Working block hours in old representation
		this.timeFrom = this.$ctx.data('time-from') || 8;
		this.timeTo = this.$ctx.data('time-to') || 17;

		// Calculate the length of a working day
		this.constraints.workDuration = scDate.toHours(this.timeTo) -
			scDate.toHours(this.timeFrom);

		// Options to skip time in work blocks
		this.constraints.wPeriod = scDate.toHours(
			this.sdxTimePicker ? this.$ctx.data('time-available-from') : 0,
		);

		// Create a new client validation instance
		this.cv = ScForm.createClientValidation({
			constraintData: this.constraints,
		} as any as ScForm.ValidationData);

		// Get options for client validation (twice in source?)
		const clientvalidation = $field.data('clientvalidation') || {};
		// Upgrade client validation constraints config
		// Be used by client validation `validateDate`
		clientvalidation.constraintData = this.constraints;

		this.isReadOnly = $input.prop('readonly');

		this.showCurrentDate = $input.data('show-current-date');
		this.$timeContainer = this.$ctx.find('.time-container');

		if (!this.isReadOnly) {
			setFlatpickrConstraints($input);
		}

		if (this.showCurrentDate && !$input.val()) {
			// Sets date in format `DD.MM.YYYY`
			$input[0].setAttribute('value', scDate.format(now));
		}

		if (this.sdxTimePicker) {
			if($input.val()) {
				this.onSelectListener($input.val() as string);
			} else {
				this.sdxTimePicker?.disable();
			}
		} else {
			this.createTimes();
		}
		$input[0].changeCallback = this.inputCallback.bind(this);
		callback();
	}

	private inputCallback(value : string) {
			const $input = $('sdx-input[type="date"]', this.$ctx);

			$input[0].setAttribute('value', value);
			this.$hiddenInput.val(value);
			this.onSelectListener(value);
	}

	/**
	 * Create an array of appointments for the given date/day
	 * Optionally you may pass a minimal starting date/time
	 */
	private getAppointmentTimes(
		date : Date,
		config : ScForm.DateConstraintData,
	) : number[] {

		// Get list of open/close pairs for the specific day
		const workBlocks = scDate.getOpeningHours(date, this.cv.constraintData!);

		// Maybe we have some holiday?
		if (!workBlocks || workBlocks === true) { return []; }

		// Do calculations after easy checks
		const now = config.now || new Date();
		const interval = config.interval || 0.5;
		const inclusive = !!config.includeClosingTime;
		const min = scDate.getMinDate(now, config);

		// Compare the day parts of the date objects
		const cmpDays = scDate.compareDays(date, min);

		// No appointments in the past
		if (cmpDays < 0) { return []; }

		// Set time to zero now to make all times available
		// Only needed if min and date are on the same day
		const startTime = cmpDays ?
			0 : Math.max(
				scDate.toHours(min),
				scDate.toHours(date),
			);

		// Create result array
		const appointments = [];

		for (let i = 0; i < workBlocks.length; i += 2) {
			// Note: values should already be in that form
			const blockStart = scDate.toHours(workBlocks[i + 0]);
			let	blockStop = scDate.toHours(workBlocks[i + 1]);
			// Check if we should include the closing time
			if (!inclusive) { blockStop -= interval; }
			// Now loop through this workblock by given interval
			// The last block time was deliberately left out here
			for (let t = blockStart; t <= blockStop; t += interval) {
				// Add everything after the startTime
				if (t >= startTime) { appointments.push(t * 60); }
			}
		}

		// Return array now
		return appointments;
	}

	// Create
	private createTimes(dateString? : string) {
		// Invalid input
		if (!dateString) {
			this.sdxTimePicker?.disable();

			return;
		}

		this.constraints.refTimeField = this.sdxTimePicker?.element;

		// Allow to pass a day date string
		// We are mostly called on input values
		const date = scDate.parseDateDay(dateString);

		// Helper function to create a drop-down option
		function createOption(name : string, value : string, selected = false) {
			// Create an option and set mandatory options
			const option = jQuery('<OPTION>').val(value).html(name);
			// Optionally set the selected attribute
			if (selected) { option.attr('selected', 'selected'); }

			// Return dom node
			return option[0];
		}

		// Helper to convert minutes to strings
		function minutesToString(minutes : number) {

			return [
				zeroFill(Math.floor(minutes / 60), 2),
				zeroFill(Math.floor(minutes) % 60, 2),
			].join(':');
		}

		const interval = this.timeInterval;
		const config = this.cv.constraintData!;

		// Get array of all available appointments that day
		const times = this.getAppointmentTimes(date as Date, config);

		// Dom node store
		const options = [];

		// Create a select dropdown
		if (times && times.length && this.sdxTimePicker) {

			// Get the text to put on top of the drop-down
			const slctTxt = this.sdxTimePicker.element.dataset.pleaseSelect;
			const defaultVal = this.sdxTimePicker.element.dataset.defaultValue;

			// Create a first option to enable de-selecting
			if (slctTxt) { options.push(createOption(slctTxt, '')); }

			// Create options for all available times
			for (const time of times) {
				// Create main label for start time
				let txt = minutesToString(time);
				// Maybe show a `from - to` interval
				if (this.showTimeInterval) {
					// Append the stop time to show full range with label
					txt = [txt, minutesToString(time + interval)].join(' - ');
				}
				// Check if entry should be selected
				const val = minutesToString(time);
				const	selected = (val === defaultVal);
				// Append the final option to our array
				options.push(createOption(txt, val, selected));
			}
		}

		// Clear and append the new options
		clear(this.sdxTimePicker?.element)?.append(...options);
		this.sdxTimePicker?.reload();

		// Enable/disable time field
		if (times && times.length) {
			this.sdxTimePicker?.enable();
		} else {
			this.sdxTimePicker?.disable();
		}

	}

	private onSelectListener(date : string) {
		if (!this.sdxTimePicker) {
			return;
		}
		if (!this.cv.validate(date)) {
			this.sdxTimePicker?.disable();

			return;
		}
		// Update drop-down
		this.createTimes(date);
	}
}

function setFlatpickrConstraints($el : JQuery) {
	const yearFrom = $el.data('year-from') || '';
	const yearTo = $el.data('year-to') || '';
	const noWeekDays : number[] = $el.data('no-weekdays') || [];
	const excludeDays = $el.data('exclude-days') || [];
	const enableFrom = $el.data('enable-from');
	const enableUntil = $el.data('enable-until');
	const availableDaysInWeek : number[] = [];
	let daysConstraint = [];

	if(yearTo && yearFrom) {
		($el[0] as any).flatpickrOptions = {
			disable: excludeDays,
			maxDate: '31.12.' + yearTo,
			minDate: '01.01.' + yearFrom,
		};
	}

	if(enableFrom !== -1 && enableUntil !== -1) {
		($el[0] as any).flatpickrOptions = {
			disable: excludeDays,
			maxDate: (new Date() as any).fp_incr(enableUntil),
			minDate: (new Date() as any).fp_incr(enableFrom),
		};
	}

	if(noWeekDays.length > 0) {
		for (const noWeekDay of noWeekDays) {
			availableDaysInWeek.push(noWeekDay - 1);
		}

		daysConstraint = excludeDays.concat([
			function(date : any) {
				return availableDaysInWeek.indexOf(date.getDay()) >= 0;
			},
		]);

		($el[0] as any).flatpickrOptions = {
			disable: daysConstraint,
		};
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/sdx/Tc.Module.SDXDatePicker.ts